import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { QuoteLineItemRow, QuoteRow } from "@/hooks/useQuotes";
import { SortableLineItemList } from "@/components/quotes/SortableLineItemList";
import { formatMoney } from "./drawerLogic";
import { DietaryTags, ItemEditRow } from "./ItemEditRow";
import { ItemAddBar } from "./ItemAddBar";
import { TemplatePopover } from "./TemplatePopover";
import type { QuoteDrawerDraft } from "./useQuoteDrawerDraft";

interface ItemsSectionProps {
  quote: QuoteRow;
  draft: Pick<QuoteDrawerDraft, "editing" | "canEdit" | "fields" | "lines" | "loadTemplate" | "startEdit">;
  dietaryLabels: (menuItemId: string | null | undefined) => string[];
  search: string;
  onSearchChange: (value: string) => void;
  /** For the drawer's Esc guard during a keyboard drag. */
  onDraggingChange?: (dragging: boolean) => void;
}

function calcText(li: Pick<QuoteLineItemRow, "pricing_type" | "quantity" | "unit_price">): string {
  const q = Number(li.quantity) || 0;
  const price = formatMoney(Number(li.unit_price) || 0);
  if (li.pricing_type === "flat") return q > 1 ? `${q} × ${price}` : "Flat";
  return li.pricing_type === "per_person" ? `${q} guests × ${price}` : `${q} × ${price}`;
}

function ViewRow({ li, tags }: { li: QuoteLineItemRow; tags: string[] }) {
  const [all, setAll] = useState(false);
  const desc = li.description?.trim() ?? "";
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3.5 gap-y-0.5 border-t px-3.5 py-2.5 first:border-t-0">
      <div className="min-w-0">
        <h4 className="text-[14px] font-semibold">{li.name}<DietaryTags tags={tags} /></h4>
        {desc && (
          <div className={`whitespace-pre-line text-[12.5px] text-muted-foreground ${all ? "" : "line-clamp-2"}`}>{li.description}</div>
        )}
        {desc.length > 90 && (
          <button type="button" onClick={() => setAll((a) => !a)} className="text-[12px] font-semibold text-accent">
            {all ? "Show less" : "Show all"}
          </button>
        )}
      </div>
      <div className="text-right tabular-nums">
        <div className="font-bold">{formatMoney(Number(li.line_total) || 0)}</div>
        <div className="text-[12.5px] text-muted-foreground">{calcText(li)}</div>
      </div>
    </div>
  );
}

/** The blue bar above Items on a Square quote (#1559). */
export function SquareBar({ onView, onResync, resyncing, resyncDisabled }: {
  onView: () => void; onResync: () => void; resyncing?: boolean; resyncDisabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2 rounded-[9px] bg-status-info-soft px-3 py-2 text-[13px] font-semibold text-status-info">
      <span>Imported from Square.</span>
      <span className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="h-8 bg-card text-foreground" onClick={onView}>View in Square</Button>
        <Button type="button" variant="outline" size="sm" className="h-8 bg-card text-foreground" onClick={onResync} disabled={resyncing || resyncDisabled}>
          Resync
        </Button>
      </span>
    </div>
  );
}

/** Items: the saved list, or the edit rows and the add bar (#1559). */
export function ItemsSection({ quote, draft, dietaryLabels, search, onSearchChange, onDraggingChange }: ItemsSectionProps) {
  const { editing, lines } = draft;
  const saved = quote.line_items ?? [];
  const head = editing ? (
    <span className="text-[12.5px] text-muted-foreground">Editing</span>
  ) : quote.square_source ? (
    <span className="text-[12.5px] text-muted-foreground">Change it in Square, then Resync</span>
  ) : draft.canEdit ? (
    <Button type="button" variant="outline" size="sm" className="h-8" onClick={draft.startEdit}>Edit quote</Button>
  ) : null;
  return (
    <section data-section="items" className="rounded-[10px] border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Items</h3>
        {head}
      </div>
      {!editing ? (
        saved.length === 0 ? (
          <div className="grid justify-items-center gap-2.5 px-3.5 py-5 text-center text-muted-foreground">
            <div>No items yet.</div>
            {draft.canEdit && <Button type="button" variant="outline" onClick={draft.startEdit}>Add items</Button>}
          </div>
        ) : (
          <div>{saved.map((li) => <ViewRow key={li.id} li={li} tags={dietaryLabels(li.menu_item_id)} />)}</div>
        )
      ) : (
        <>
          {lines.lineItems.length === 0 ? (
            <div className="px-3.5 py-5 text-center text-muted-foreground">Search or browse the catalog below, or load a template.</div>
          ) : (
            <SortableLineItemList
              items={lines.lineItems}
              onChange={lines.updateLine}
              onRemove={lines.removeLine}
              onReorder={lines.reorderLines}
              onDraggingChange={onDraggingChange}
              renderRow={(item, index, handle) => (
                <ItemEditRow item={item} index={index} handle={handle} tags={dietaryLabels(item.menu_item_id)}
                  onChange={lines.updateLine} onRemove={lines.removeLine} onUnitMode={lines.setUnitMode} />
              )}
            />
          )}
          <ItemAddBar
            guests={Number(quote.guest_count) || 0}
            search={search}
            onSearchChange={onSearchChange}
            onPick={lines.addPickedItem}
            onCustom={lines.addCustomLine}
          >
            <TemplatePopover itemCount={lines.lineItems.length} onLoad={draft.loadTemplate} />
          </ItemAddBar>
        </>
      )}
    </section>
  );
}
